import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from './config';
import N8nImportModal from './N8nImportModal';
import N8nCatalogModal from './N8nCatalogModal';
import { formatLocalDateTime } from './timezone';

const containerStyle = {
  padding: '40px',
  maxWidth: '1000px',
  margin: '0 auto',
  width: '100%',
  color: 'var(--text-primary)',
};

const cardStyle = {
  background: 'var(--bg-secondary)',
  padding: '20px',
  borderRadius: '8px',
  border: '1px solid var(--border-color)',
  marginBottom: '20px',
};

const btnStyle = (bg, disabled = false) => ({
  padding: '8px 16px',
  background: disabled ? 'var(--bg-tertiary)' : bg,
  color: disabled ? 'var(--text-tertiary)' : 'var(--text-inverse)',
  border: 'none',
  borderRadius: '4px',
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontSize: '13px',
  fontWeight: '500',
  marginRight: '8px',
});

const STATUS_COLORS = {
  trusted: 'var(--success-color)',
  untrusted: 'var(--warning-color)',
  blocked: 'var(--error-color)',
};

const STATUS_LABELS = {
  trusted: '✅ Trusted',
  untrusted: '⚠️ Untrusted',
  blocked: '🚫 Blocked',
};

function BadgeStatus({ status }) {
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 10px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: '600',
      background: STATUS_COLORS[status] + '22',
      color: STATUS_COLORS[status],
      border: `1px solid ${STATUS_COLORS[status]}`,
    }}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

export default function PluginGovernance({ onBack }) {
  const [plugins, setPlugins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState(null); // { type: 'success'|'error', text }
  const [showN8nImport, setShowN8nImport] = useState(false);
  const [showCatalog, setShowCatalog] = useState(false);
  const [expanded, setExpanded] = useState({}); // node_id → bool
  const [workspaceId, setWorkspaceId] = useState('');
  const [pinTargets, setPinTargets] = useState({}); // node_id → version_id
  const fileRef = useRef(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const showMsg = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg(null), 4000);
  };

  const fetchPlugins = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/plugins`, { headers });
      setPlugins(res.data);
    } catch (e) {
      showMsg('error', e?.response?.data?.detail || 'Failed to load plugins');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPlugins(); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const form = new FormData();
    form.append('file', file);
    try {
      const res = await axios.post(`${API_BASE_URL}/plugins/upload`, form, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      showMsg('success', res.data.message || 'Plugin installed');
      fetchPlugins();
    } catch (e) {
      showMsg('error', e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const setTrust = async (nodeId, newStatus) => {
    try {
      await axios.put(`${API_BASE_URL}/plugins/${nodeId}/trust`, { status: newStatus }, { headers });
      showMsg('success', `Trust status set to '${newStatus}'`);
      fetchPlugins();
    } catch (e) {
      showMsg('error', e?.response?.data?.detail || 'Failed to update trust');
    }
  };

  const deletePlugin = async (nodeId) => {
    if (!(await window.confirm(`Delete plugin '${nodeId}'? This cannot be undone.`))) return;
    try {
      await axios.delete(`${API_BASE_URL}/plugins/${nodeId}`, { headers });
      showMsg('success', `Plugin '${nodeId}' deleted`);
      fetchPlugins();
    } catch (e) {
      showMsg('error', e?.response?.data?.detail || 'Delete failed');
    }
  };

  const reloadPlugin = async (nodeId) => {
    try {
      await axios.post(`${API_BASE_URL}/plugins/${nodeId}/reload`, {}, { headers });
      showMsg('success', `Plugin '${nodeId}' reloaded`);
      fetchPlugins();
    } catch (e) {
      showMsg('error', e?.response?.data?.detail || 'Reload failed');
    }
  };

  const rollback = async (nodeId, versionId) => {
    try {
      await axios.post(`${API_BASE_URL}/plugins/${nodeId}/rollback/${versionId}`, {}, { headers });
      showMsg('success', 'Rollback successful');
      fetchPlugins();
    } catch (e) {
      showMsg('error', e?.response?.data?.detail || 'Rollback failed');
    }
  };

  const pinVersion = async (nodeId) => {
    const versionId = pinTargets[nodeId] || null;
    if (!workspaceId) { showMsg('error', 'Enter a Workspace ID first'); return; }
    try {
      await axios.put(
        `${API_BASE_URL}/plugins/${nodeId}/workspace-pin`,
        { workspace_id: workspaceId, version_id: versionId || null },
        { headers },
      );
      showMsg('success', versionId ? 'Version pinned' : 'Pin cleared');
    } catch (e) {
      showMsg('error', e?.response?.data?.detail || 'Pin failed');
    }
  };

  const toggleExpand = (nodeId) =>
    setExpanded((prev) => ({ ...prev, [nodeId]: !prev[nodeId] }));

  return (
    <div style={containerStyle}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '28px', gap: '16px' }}>
        <button onClick={onBack} style={{
          padding: '8px 16px', borderRadius: '4px', fontSize: '13px', fontWeight: '500',
          background: 'transparent', border: '1px solid var(--border-color)',
          color: 'var(--text-primary)', cursor: 'pointer',
        }}>← Back</button>
        <h2 style={{ margin: 0, fontSize: '22px' }}>🧩 Plugin Governance</h2>
      </div>

      {msg && (
        <div style={{
          padding: '12px 16px',
          borderRadius: '6px',
          marginBottom: '16px',
          background: msg.type === 'success' ? 'var(--success-color)22' : 'var(--error-color)22',
          color: msg.type === 'success' ? 'var(--success-color)' : 'var(--error-color)',
          border: `1px solid ${msg.type === 'success' ? 'var(--success-color)' : 'var(--error-color)'}`,
        }}>
          {msg.text}
        </div>
      )}

      {/* Upload section */}
      <div style={cardStyle}>
        <h3 style={{ margin: '0 0 12px', fontSize: '16px' }}>Upload Plugin Package</h3>
        <p style={{ color: 'var(--text-secondary)', fontSize: '13px', margin: '0 0 12px' }}>
          Upload a <code>.zip</code> file containing <code>node.json</code> and the plugin script.
          Plugins install without server restart and start as <strong>untrusted</strong> — review
          and promote to <em>trusted</em> to run them in-process, or leave as untrusted for
          sandboxed subprocess execution.
        </p>
        <input
          ref={fileRef}
          type="file"
          accept=".zip"
          style={{ display: 'none' }}
          onChange={handleUpload}
        />
        <button
          style={btnStyle('var(--info-color)', uploading)}
          disabled={uploading}
          onClick={() => fileRef.current?.click()}
        >
          {uploading ? 'Uploading…' : '📦 Upload .zip'}
        </button>
        <button
          style={{ ...btnStyle('var(--accent-color)'), marginTop: '8px' }}
          onClick={() => setShowCatalog(true)}
        >
          📦 Browse n8n Catalog
        </button>
        <button
          style={{ ...btnStyle('var(--bg-tertiary)'), marginTop: '6px', color: 'var(--text-secondary)' }}
          onClick={() => setShowN8nImport(true)}
        >
          🔌 Import from Descriptor JSON
        </button>
      </div>

      {showCatalog && (
        <N8nCatalogModal
          onClose={() => setShowCatalog(false)}
          onImported={() => { fetchPlugins(); showMsg('success', 'n8n node imported — edit the stub, then reload and trust it.'); }}
        />
      )}

      {showN8nImport && (
        <N8nImportModal
          onClose={() => setShowN8nImport(false)}
          onImported={() => { fetchPlugins(); showMsg('success', 'n8n node imported — edit the stub, then reload and trust it.'); }}
        />
      )}

      {/* Workspace version pin helper */}
      <div style={{ ...cardStyle, display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '13px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
          Workspace ID for version pins:
        </span>
        <input
          value={workspaceId}
          onChange={(e) => setWorkspaceId(e.target.value)}
          placeholder="paste workspace id…"
          style={{
            flex: 1, minWidth: '220px', padding: '7px 10px', borderRadius: '4px',
            border: '1px solid var(--border-color)', background: 'var(--bg-input)',
            color: 'var(--text-primary)', fontSize: '13px',
          }}
        />
      </div>

      {/* Plugin list */}
      {loading ? (
        <p style={{ color: 'var(--text-secondary)' }}>Loading plugins…</p>
      ) : plugins.length === 0 ? (
        <div style={{ ...cardStyle, textAlign: 'center', color: 'var(--text-secondary)', padding: '40px' }}>
          No plugins installed. Upload a <code>.zip</code> to get started.
        </div>
      ) : (
        plugins.map((p) => (
          <div key={p.node_id} style={cardStyle}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '28px' }}>{p.icon || '🧩'}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <strong style={{ fontSize: '15px' }}>{p.name}</strong>
                  <code style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{p.node_id}</code>
                  <BadgeStatus status={p.status} />
                  {p.current_version && (
                    <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>v{p.current_version}</span>
                  )}
                </div>
                {p.description && (
                  <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--text-secondary)' }}>
                    {p.description}
                  </p>
                )}
                <p style={{ margin: '4px 0 0', fontSize: '11px', color: 'var(--text-tertiary)' }}>
                  Category: {p.category} &nbsp;·&nbsp;
                  Checksum: <code>{p.checksum ? p.checksum.slice(0, 12) + '…' : '—'}</code> &nbsp;·&nbsp;
                  Installed: {p.installed_at ? formatLocalDateTime(p.installed_at) : '—'}
                </p>
              </div>
            </div>

            {/* Trust controls */}
            <div style={{ marginTop: '14px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              <button
                style={btnStyle('var(--success-color)', p.status === 'trusted')}
                disabled={p.status === 'trusted'}
                onClick={() => setTrust(p.node_id, 'trusted')}
              >
                ✅ Trust
              </button>
              <button
                style={btnStyle('var(--warning-color)', p.status === 'untrusted')}
                disabled={p.status === 'untrusted'}
                onClick={() => setTrust(p.node_id, 'untrusted')}
              >
                ⚠️ Untrust
              </button>
              <button
                style={btnStyle('var(--error-color)', p.status === 'blocked')}
                disabled={p.status === 'blocked'}
                onClick={() => setTrust(p.node_id, 'blocked')}
              >
                🚫 Block
              </button>
              <button
                style={btnStyle('var(--info-color)')}
                onClick={() => reloadPlugin(p.node_id)}
              >
                🔄 Reload
              </button>
              <button
                style={btnStyle('var(--bg-tertiary)')}
                onClick={() => toggleExpand(p.node_id)}
              >
                {expanded[p.node_id] ? '▲ Hide Details' : '▼ Show Versions & Pin'}
              </button>
              <button
                style={{ ...btnStyle('var(--error-color)'), marginLeft: 'auto' }}
                onClick={() => deletePlugin(p.node_id)}
              >
                🗑 Delete
              </button>
            </div>

            {/* Expanded: version history + workspace pin */}
            {expanded[p.node_id] && (
              <div style={{ marginTop: '16px' }}>
                {/* Version history */}
                <h4 style={{ margin: '0 0 8px', fontSize: '13px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Version History
                </h4>
                {p.versions && p.versions.length > 0 ? (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', marginBottom: '16px' }}>
                    <thead>
                      <tr style={{ color: 'var(--text-secondary)', borderBottom: '1px solid var(--border-color)' }}>
                        <th style={{ textAlign: 'left', padding: '4px 8px' }}>Version</th>
                        <th style={{ textAlign: 'left', padding: '4px 8px' }}>Checksum</th>
                        <th style={{ textAlign: 'left', padding: '4px 8px' }}>Installed</th>
                        <th style={{ textAlign: 'left', padding: '4px 8px' }}>Active</th>
                        <th style={{ textAlign: 'left', padding: '4px 8px' }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {p.versions.map((v) => (
                        <tr key={v.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                          <td style={{ padding: '4px 8px', fontWeight: v.is_active ? '600' : '400' }}>
                            {v.version}
                          </td>
                          <td style={{ padding: '4px 8px', color: 'var(--text-tertiary)' }}>
                            <code>{v.checksum?.slice(0, 12)}…</code>
                          </td>
                          <td style={{ padding: '4px 8px', color: 'var(--text-tertiary)' }}>
                            {v.installed_at ? formatLocalDateTime(v.installed_at) : '—'}
                          </td>
                          <td style={{ padding: '4px 8px' }}>
                            {v.is_active && <span style={{ color: 'var(--success-color)' }}>● Active</span>}
                          </td>
                          <td style={{ padding: '4px 8px' }}>
                            {!v.is_active && (
                              <button
                                style={btnStyle('var(--info-color)')}
                                onClick={() => rollback(p.node_id, v.id)}
                              >
                                ↩ Rollback
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p style={{ color: 'var(--text-tertiary)', fontSize: '13px', marginBottom: '16px' }}>No version history.</p>
                )}

                {/* Workspace version pin */}
                <h4 style={{ margin: '0 0 8px', fontSize: '13px', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                  Workspace Version Pin
                </h4>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <select
                    value={pinTargets[p.node_id] || ''}
                    onChange={(e) => setPinTargets((prev) => ({ ...prev, [p.node_id]: e.target.value }))}
                    style={{
                      padding: '6px 10px', borderRadius: '4px',
                      border: '1px solid var(--border-color)',
                      background: 'var(--bg-input)', color: 'var(--text-primary)', fontSize: '13px',
                    }}
                  >
                    <option value="">— pin to latest —</option>
                    {(p.versions || []).map((v) => (
                      <option key={v.id} value={v.id}>{v.version} ({v.checksum?.slice(0, 8)}…)</option>
                    ))}
                  </select>
                  <button
                    style={btnStyle('var(--info-color)', !workspaceId)}
                    disabled={!workspaceId}
                    onClick={() => pinVersion(p.node_id)}
                  >
                    📌 {pinTargets[p.node_id] ? 'Pin Version' : 'Clear Pin'}
                  </button>
                </div>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}
